import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import _slicedToArray from 'babel-runtime/helpers/slicedToArray';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import React, { useReducer, useCallback, useMemo, useEffect, Fragment } from 'react';
import Sticky from 'react-stickynode';
import PropTypes from 'prop-types';

import BedVisualizerGallery from '../../components/BedVisualizerGallery/BedVisualizerGallery';
import VisualizerCategory from '../../components/VisualizerCategory/VisualizerCategory';
import VisualizerRoomCategory from '../../components/VisualizerCategory/VisualizerRoomCategory';
import WallVisualizerGallery from '../../components/WallVisualizerGallery/WallVisualizerGallery';

import VisualizerMySelections from '../../components/VisualizerMySelections/VisualizerMySelections';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import VisualizerModal from '../../components/VisualizerModal/VisualizerModal';
import VisualizerSave from '../../components/VisualizerSave/VisualizerSave';
import VisualizerSaveModal from '../../components/VisualizerSaveModal/VisualizerSaveModal';
import useWindowWidth from '../../hooks/useWindowWidth';

import { getVendorCustomAttribute } from '../../Utils/CartUtils';
import { addProductToSelection, getCategoriesWithProducts, updateQueryParams, getSelectedProductsFromQueryParams } from '../../Utils/VisualizerUtils';

import { StyledVisualizerWrapper, StyledVisualizerTitle, StyledVisualizerSubtitle, StyledVisualizerHeader, StyledAddToCartButton } from './VisualizerStyles';

import camelCase from 'lodash.camelcase';

var visualizerReducer = function visualizerReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_SELECTED_PRODUCTS':
      return Object.assign({}, state, {
        selectedProducts: action.updatedProducts,
        discontinuedProductCount: action.discontinuedProductCount
      });

    case 'REMOVE_ALL_SELECTED_PRODUCTS':
      return Object.assign({}, state, {
        selectedProducts: []
      });
    case 'ADDING_TO_CART':
      return Object.assign({}, state, {
        addingToCart: true
      });
    case 'ADDED_TO_CART':
      return Object.assign({}, state, {
        addingToCart: false
      });

    case 'SET_STICKY_GALLERY':
      return Object.assign({}, state, {
        stickyMobileGallery: true
      });
    case 'REMOVE_STICKY_GALLERY':
      return Object.assign({}, state, {
        stickyMobileGallery: false
      });

    case 'SET_THUMBNAIL':
      return Object.assign({}, state, {
        largeThumbnail: action.largeThumbnail
      });

    case 'SET_BACKGROUND':
      return Object.assign({}, state, {
        background: action.background
      });

    case 'SET_TEMPLATE':
      return Object.assign({}, state, {
        template: action.template
      });
    case 'TOGGLE_SAVE_MODAL':
      return Object.assign({}, state, {
        showSaveModal: !state.showSaveModal
      });
    case 'CATEGORY_ACTIVE':
      return Object.assign({}, state, {
        categoryActive: Object.assign({}, state.categoryActive, _defineProperty({}, action.categoryActive, !state.categoryActive[action.categoryActive]))
      });
    default:
      return state;
  }
};

var _ref = React.createElement(LoadingSpinner, null);

var _ref2 = React.createElement('div', null);

var Visualizer = function Visualizer(props) {
  var _useReducer = useReducer(visualizerReducer, {
    selectedProducts: [],
    discontinuedProductCount: 0,
    stickyMobileGallery: false,
    addingToCart: false,
    largeThumbnail: false,
    showSaveModal: false,
    background: '',
    categoryActive: {
      collections: false,
      beddings: false,
      sheets: false,
      decorativePillows: false,
      accentPillows: false,
      euroPillows: false,
      throws: false,
      headboards: false,
      '12x12Posters': false,
      '16x20Posters': false,
      '18x18Posters': false,
      '18x24Posters': false,
      '9x12Posters': false,
      wallpaper: false,
      wireLights: false,
      woodenSign: false
    }
  }),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      dispatch = _useReducer2[1];

  var categories = useMemo(function () {
    return getCategoriesWithProducts(props.categoryData, props.products);
  }, [props.categoryData, props.products]);

  var clearAllProducts = function clearAllProducts() {
    // remove selected query string
    updateQueryParams({ products: [], queryParams: props.queryParams });
  };

  var setBackground = useCallback(function (background) {
    dispatch({ type: 'SET_BACKGROUND', background: background });
  }, [dispatch]);

  var setLargeThumbnail = useCallback(function (largeThumbnail) {
    return dispatch({
      type: 'SET_THUMBNAIL',
      largeThumbnail: largeThumbnail
    });
  }, [dispatch]);
  var toggleSaveModal = useCallback(function () {
    return dispatch({
      type: 'TOGGLE_SAVE_MODAL'
    });
  }, [dispatch]);

  var windowWidth = useWindowWidth();
  var isWallGallery = props.slug === 'wall-visualizer';

  var handleProductSelection = function handleProductSelection(product) {
    var updatedProducts = [].concat(_toConsumableArray(state.selectedProducts));
    if (state.selectedProducts.find(function (prod) {
      return prod.variantId === product.variantId;
    })) {
      updatedProducts = updatedProducts.filter(function (prod) {
        return prod.variantId != product.variantId;
      });
      updateQueryParams({
        products: updatedProducts,
        queryParams: props.queryParams
      });
    } else {
      var newProducts = addProductToSelection(updatedProducts, categories, product);
      updateQueryParams({
        products: newProducts,
        queryParams: props.queryParams
      });
    }
  };

  useEffect(function () {
    var _getSelectedProductsF = getSelectedProductsFromQueryParams(props.queryParams, props.products, setBackground),
        selectedProducts = _getSelectedProductsF.selectedProducts,
        discontinuedProductCount = _getSelectedProductsF.discontinuedProductCount;

    dispatch({
      type: 'UPDATE_SELECTED_PRODUCTS',
      updatedProducts: selectedProducts,
      discontinuedProductCount: discontinuedProductCount
    });
    // adding windowWidth to force dispatch on resize to resize wall vis
  }, [props.queryParams, props.products, windowWidth, setBackground]);

  var handleAddToCart = function handleAddToCart() {
    var availableProducts = state.selectedProducts.filter(function (prod) {
      return prod.availableForSale;
    });
    var addToCartItems = availableProducts.map(function (product) {
      var customAttributes = getVendorCustomAttribute(product.vendor);
      customAttributes.push({
        key: '_cc',
        value: isWallGallery ? 'Wall Visualizer' : 'Built by me'
      });
      return {
        variantId: product.variantId,
        quantity: 1,
        customAttributes: customAttributes
      };
    });
    dispatch({ type: 'ADDING_TO_CART' }); // loading spinner enabled
    props.addVariantToCart(addToCartItems, function () {
      return dispatch({ type: 'ADDED_TO_CART' });
    } // loading spinner disabled
    );
  };

  var handleStateChange = function handleStateChange(status) {
    if (status.status === Sticky.STATUS_FIXED) {
      dispatch({ type: 'SET_STICKY_GALLERY' });
    } else {
      dispatch({ type: 'REMOVE_STICKY_GALLERY' });
    }
  };

  var handleCategoryActive = function handleCategoryActive(category) {
    dispatch({
      type: 'CATEGORY_ACTIVE',
      categoryActive: category
    });
  };
  return React.createElement(
    Fragment,
    null,
    React.createElement(
      StyledVisualizerWrapper,
      { className: 'VisualizerWrapper' },
      React.createElement(
        Sticky,
        {
          innerZ: 1 // need this to get dragged images over other items
          , top: windowWidth > 769 ? 120 : 93,
          bottomBoundary: windowWidth > 769 ? '.VisualizerWrapper' : 'body',
          onStateChange: handleStateChange,
          enableTransforms: isWallGallery ? false : true
        },
        isWallGallery ? React.createElement(WallVisualizerGallery, {
          stickyMobileGallery: state.stickyMobileGallery,
          selectedProducts: state.selectedProducts,
          largeThumbnail: state.largeThumbnail,
          setLargeThumbnail: setLargeThumbnail,
          handleProductSelection: handleProductSelection,
          windowWidth: windowWidth,
          queryParams: props.queryParams,
          setBackground: setBackground,
          background: state.background,
          handleClearAll: clearAllProducts
        }) : React.createElement(BedVisualizerGallery, {
          stickyMobileGallery: state.stickyMobileGallery,
          selectedProducts: state.selectedProducts,
          largeThumbnail: state.largeThumbnail,
          setLargeThumbnail: setLargeThumbnail,
          handleClearAll: clearAllProducts,
          windowWidth: windowWidth,
          queryParams: props.queryParams,
          isStaging: props.isStaging
        })
      ),
      React.createElement(
        'div',
        null,
        React.createElement(VisualizerSave, {
          toggleSaveModal: toggleSaveModal,
          toggleAccountModal: props.toggleAccountModal,
          queryParams: props.queryParams,
          user: props.user,
          selectedProducts: state.selectedProducts,
          topSection: true
        }),
        React.createElement(
          StyledVisualizerTitle,
          null,
          props.title
        ),
        React.createElement(
          StyledVisualizerSubtitle,
          null,
          props.subTitle
        ),
        React.createElement(
          StyledVisualizerHeader,
          null,
          props.categoryHeader
        ),
        React.createElement(
          'ul',
          null,
          categories.sort(function (a, b) {
            return a.ind - b.ind;
          }) // TODO: temp fix because you cant order items in firestore, need CMS for better content editing
          .map(function (categoryItem, index) {
            if (categoryItem.title === 'collections') {
              return React.createElement(VisualizerRoomCategory, {
                key: categoryItem.title,
                categoryItem: categoryItem,
                handleProductSelection: handleProductSelection,
                selectedProducts: state.selectedProducts,
                firstCategory: index === 0,
                isStaging: props.isStaging,
                rooms: props.rooms,
                active: state.categoryActive[camelCase(categoryItem.title)],
                handleCategoryActive: handleCategoryActive,
                title: camelCase(categoryItem.title),
                categoryActive: state.categoryActive
              });
            } else {
              return React.createElement(VisualizerCategory, {
                key: categoryItem.title,
                categoryItem: categoryItem,
                handleProductSelection: handleProductSelection,
                selectedProducts: state.selectedProducts,
                firstCategory: index === 0,
                isStaging: props.isStaging,
                active: state.categoryActive[camelCase(categoryItem.title)],
                handleCategoryActive: handleCategoryActive,
                title: camelCase(categoryItem.title)
              });
            }
          })
        ),
        React.createElement(VisualizerMySelections, {
          selectedProducts: state.selectedProducts,
          handleProductSelection: handleProductSelection,
          handleClearAll: clearAllProducts
        }),
        React.createElement(
          StyledAddToCartButton,
          { onClick: handleAddToCart },
          state.addingToCart ? _ref : 'Add To Cart'
        ),
        React.createElement(VisualizerSave, {
          toggleSaveModal: toggleSaveModal,
          toggleAccountModal: props.toggleAccountModal,
          queryParams: props.queryParams,
          user: props.user,
          selectedProducts: state.selectedProducts
        })
      ),
      _ref2
    ),
    React.createElement(VisualizerModal, {
      discontinuedProductCount: state.discontinuedProductCount || 0
      // remove discountinued count to remove modal
      , closeModalFunc: function closeModalFunc() {
        return dispatch({
          type: 'UPDATE_SELECTED_PRODUCTS',
          updatedProducts: state.selectedProducts,
          discontinuedProductCount: 0
        });
      }
    }),
    state.showSaveModal && React.createElement(VisualizerSaveModal, {
      active: state.showSaveModal,
      closeModalFunc: toggleSaveModal,
      queryParams: props.queryParams,
      user: props.user,
      selectedProducts: state.selectedProducts,
      isWallGallery: isWallGallery
    })
  );
};

Visualizer.propTypes = {
  products: PropTypes.array.isRequired,
  rooms: PropTypes.array.isRequired,
  addVariantToCart: PropTypes.func.isRequired,
  checkout: PropTypes.object,
  categoryData: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  categoryHeader: PropTypes.string.isRequired,
  queryParams: PropTypes.string,
  user: PropTypes.object,
  toggleAccountModal: PropTypes.func.isRequired,
  slug: PropTypes.string.isRequired,
  isStaging: PropTypes.bool.isRequired
};

export default Visualizer;